import { Routes } from '@angular/router';

import { AuthGuard } from '@core/guards/auth.guard';
import {
  briefingsSurfaceGuard,
  estimatorSurfaceGuard,
} from '@core/config/app-surface.service';
import { ErrorTemplateHandlingComponent } from './components/error-template-handling/error-template-handling.component';
import { HandingFunnelGuard } from './guards/handling-estimate-funnel.guard';
import { optionalUserResolver } from '@core/guards/optional-user.resolver';
import { provideAuthConfig } from '@zitemedia/zitadel-sso-frontend';
import { environment } from '@environments/environment';
import { v2BriefingRoutes } from '@v2/briefing/v2-briefing.routes';
import { v2ContentRoutes } from '@v2/content/v2-content.routes';


/**
 * The split is strict: each URL has exactly one canonical host, and there is no
 * page reachable at both (design doc, §3).
 *
 * `canMatch` and not the `**` wildcard, which cannot express it -- the wildcard
 * only ever sees a path that matched nothing, so every route the estimator
 * table claims would keep rendering on the briefings host. What is left
 * unguarded is exactly what §3 says the briefings host serves: the two briefing
 * pages, `/faq`, `/terms`, `/contacts`, and the wildcard that sends everything
 * else to the estimator.
 */
export const coreRoutes: Routes = [
  {
    path: 'login',
    // Estimator-only, though §3 lists /login under the briefings host: §6.2 is
    // the section with the mechanism behind it, and login genuinely cannot
    // complete anywhere else. ZITADEL_REDIRECT_URI is pinned to the estimator
    // origin, so a sign-in begun on briefings.* returns to estimator.* whatever
    // the address bar said at the start. On the briefings host this path now
    // falls to the surface redirect below, which is the same destination said
    // honestly and in one hop.
    canMatch: [estimatorSurfaceGuard],
    loadComponent: () => import('app/login-wrapper/login-wrapper.component').then(m => m.LoginWrapperComponent),
    providers: [
      provideAuthConfig({
        apiRoot: environment.API_ROOT.replace(/\/api\/?$/, ''),
        silentAuth: true,
        successCallbackUrl: '/',
        errorCallbackUrl: '/login',
      }),
    ],
    data: {
      pageName: 'EVO Apps | Login',
      // The page owns the full viewport: it is a navy stand against a light
      // gate, and the old header, sidebar and footer have nothing to add to a
      // screen with one button on it. `v2` is CoreComponent's existing escape
      // hatch to a bare outlet — see core.component.html.
      v2: true
    },
  },
  {
    path: 'login-password',
    canMatch: [estimatorSurfaceGuard],
    loadChildren: () => import('app/login/login.module').then(m => m.LoginModule),
    data: {
      pageName: 'EVO Apps | Password Login'
    },
  },
  {
    path: 'restore-pass',
    canMatch: [estimatorSurfaceGuard],
    loadChildren: () => import('app/restore-pass/restore-pass.module').then(m => m.RestorePassModule),
    data: {
      pageName: 'EVO Apps | Password Settings'
    },
  },
  {
    path: 'estimator-for-am',
    canMatch: [estimatorSurfaceGuard],
    loadChildren: () => import('app/handling-quote-funnel-page/handling-quote-funnel-page.module').then(m => m.HandlingQuoteFunnelPageModule),
    canActivate: [HandingFunnelGuard],
    data: {
      pageName: 'EVO Apps | Handling Estimate',
      hideHeader: true
    },
  },
  {
    path: 'handling-quote-funnel-error',
    canMatch: [estimatorSurfaceGuard],
    component: ErrorTemplateHandlingComponent
  },
  {
    // The briefings host, signed out. Declared before the guarded group so it
    // claims these paths first; `canMatch` keeps it off the estimator host,
    // where every one of these paths still requires an account.
    path: '',
    canMatch: [briefingsSurfaceGuard],
    loadComponent: () =>
      import('@v2/shell/v2-shell.component').then(m => m.V2ShellComponent),
    data: { v2: true },
    resolve: { user: optionalUserResolver },
    children: [
      ...v2BriefingRoutes,
      ...v2ContentRoutes,
    ],
  },
  {
    path: '',
    canActivate: [AuthGuard],
    children: [
      {
        // Cutover stage 1: the old app, moved wholesale under /classic.
        //
        // It is listed BEFORE the v2 empty path deliberately. An empty-path
        // route with loadChildren matches the empty prefix and then commits to
        // the lazy bundle; if v2 came first it would swallow /classic and never
        // fall back. Order is load-bearing here, not cosmetic.
        //
        // The whole subtree is the legacy *estimator*. Serving it on the
        // briefings host would put staff tooling on the client-facing domain,
        // counted in the briefings Plausible property and offered to Google as
        // canonical there.
        path: 'classic',
        canMatch: [estimatorSurfaceGuard],
        children: [
      {
        path: '',
        pathMatch: 'full',
        loadChildren: () => import('app/bulk-location-estimate/bulk-location-estimate.module').then(m => m.BulkLocationEstimateModule),
        data: {
          pageName: 'EVO Apps | Airport Fee Estimator',
          breadcrumbs: 'Airport Fee Estimator'
        },
      },
      {
        path: 'airport-briefings',
        loadChildren: () => import('app/home/home.module').then(m => m.HomeModule),
        data: {
          pageName: 'EVO Apps | Main',
          breadcrumbs: 'Airport Briefings'
        },
      },
      {
        path: 'profile',
        loadChildren: () => import('app/profile/profile.module').then(m => m.ProfileModule),
        data: {
          pageName: 'EVO Apps | Profile',
          breadcrumbs: 'Profile'
        },
      },
      {
        path: 'search',
        loadChildren: () => import('app/search-page/search-page.module').then(m => m.SearchPageModule),
        data: {
          pageName: 'EVO Apps | Airport Search',
          breadcrumbs: 'Search'
        },
      },
      {
        path: 'airport/:id',
        loadChildren: () => import('app/airport/airport.module').then(m => m.AirportModule),
        data: {
          pageName: 'private',
          breadcrumbs: 'Airport Briefings'
        },
      },
      {
        path: 'aircraft',
        loadChildren: () => import('app/aircraft/aircraft.module').then(m => m.AircraftModule),
        data: {
          pageName: 'EVO Apps | Aircraft Management',
          breadcrumbs: 'Aircraft Management'
        },
      },
      {
        path: 'airport-admin',
        loadChildren: () => import('app/airport-admin/airport-admin.module').then(m => m.AirportAdminModule),
        data: {
          pageName: 'EVO Apps | Airport Management',
          breadcrumbs: 'Airport Management'
        },
      },
      {
        path: 'handler-info-admin',
        loadChildren: () => import('app/handler-info-admin/handler-info-admin.module').then(m => m.HandlerInfoAdminModule),
        data: {
          pageName: 'EVO Apps | Handler Info Management',
          breadcrumbs: 'Handler Info Management'
        },
      },
      {
        path: 'feedback-admin',
        loadChildren: () => import('app/feedback-admin/feedback-admin.module').then(m => m.FeedbackAdminModule),
        data: {
          pageName: 'EVO Apps | Feedback Management',
          breadcrumbs: 'Feedback Management'
        },
      },
      {
        path: 'airport-fees-admin',
        loadChildren: () => import('app/airport-fees-admin/airport-fees-admin.module').then(m => m.AirportFeesAdminModule),
        data: {
          pageName: 'EVO Apps | Airport Fees Management',
          breadcrumbs: 'Airport Fees Management'
        },
      },
      {
        path: 'handler-fees',
        loadChildren: () => import('app/handler-fees/handler-fees.module').then(m => m.HandlerFeesModule),
        data: {
          pageName: 'EVO Apps | Handler Fees Management',
          breadcrumbs: 'Handler Fees Management'
        },
      },
      {
        path: 'evo-tariffs',
        loadChildren: () => import('app/evo-tariffs/evo-tariffs.module').then(m => m.EvoTariffsModule),
        data: {
          pageName: 'EVO Apps | Tariff Management',
          breadcrumbs: 'Tariff Management'
        },
      },
      {
        path: 'accounts',
        loadChildren: () => import('app/accounts/accounts.module').then(m => m.AccountsModule),
        data: {
          pageName: 'EVO Apps | Account Management',
          breadcrumbs: 'Account Management'
        },
      },
      {
        path: 'cost-estimator',
        loadChildren: () => import('app/cost-estimator/cost-estimator.module').then(m => m.CostEstimatorModule),
        data: {
          pageName: 'EVO Apps | Cost Estimator',
          breadcrumbs: 'Cost Estimator'
        },
      },
      {
        path: 'faq',
        loadChildren: () => import('app/faq/faq.module').then(m => m.FaqModule),
        data: {
          pageName: 'EVO Apps | Estimator FAQ',
          breadcrumbs: 'Estimator FAQ'
        },
      },
        ],
      },
      {
        // v2 is the app. Empty path, so it takes everything /classic did not.
        // The { v2: true } escape hatch is keyed on route data rather than the
        // path, so it keeps working across this move unchanged.
        path: '',
        loadChildren: () => import('app/v2/v2.routes').then(m => m.v2Routes),
      },
    ]
  },
  {
    // A stray path on the briefings host belongs to the estimator. Declared
    // before the error page so it wins on that host, and guarded so the
    // estimator host still gets a real 404.
    path: '**',
    canMatch: [briefingsSurfaceGuard],
    loadComponent: () =>
      import('@core/components/surface-redirect/surface-redirect.component').then(
        m => m.SurfaceRedirectComponent,
      ),
  },
  {
    path: '**',
    loadComponent: () => import('@core/components/error-template/error-template.component').then(m => m.ErrorTemplateComponent)
  }
];
